import React, { useEffect, useMemo } from 'react';
import { InputAdornment, IconButton } from '@mui/material';
import { connect } from 'react-redux';
import { useParams } from 'react-router-v5';
import { bindActionCreators, Dispatch } from 'redux';
import Img from 'react-cool-img';
import { IAppState } from 'spw-redux/reducer';
import { actions } from 'spw-redux/modules/auth/actions';
import Security from 'spw-static/images/security-icon.svg';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  DialogBox,
  DialogCustom,
  FormColumns,
  GradientBackgroundContainer,
  Title,
  TextField,
  ButtonCustom,
  PasswordRequirementsCard,
  ErrorMessage,
} from '../styles';

const regexRequirements = [
  /.*[a-z].*/,
  /.*[A-Z].*/,
  /.*[0-9].*/,
  // eslint-disable-next-line no-useless-escape
  /.*[.*!@#$%^&(){}\[\]:";'<>,?\\\/~`_+\-=|].*/,
];

interface IResetPasswordDispatch {
  setNewPassword: (newPassword: string) => void;
  setVerifyPassword: (verifyPassword: string) => void;
  sendVerifyPassword: (
    token: string,
    newPassword: string,
    verifyPassword: string
  ) => void;
}

interface IResetPassword {
  newPassword: string;
  verifyPassword: string;
  updatePasswordSuccess: boolean;
}

type IProps = IResetPassword & IResetPasswordDispatch;

const ResetPassword: React.FC<IProps> = ({
  newPassword,
  verifyPassword,
  setNewPassword,
  setVerifyPassword,
  sendVerifyPassword,
  updatePasswordSuccess,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = React.useState(false);
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    localStorage.removeItem('api_token_v2');
    localStorage.removeItem('user_token');
  }, []);

  const validPassword = useMemo(() => {
    if (newPassword.length > 7) {
      return regexRequirements.every((regExp) => regExp.test(newPassword));
    }

    return false;
  }, [newPassword]);
  const validVerifyPassword = newPassword === verifyPassword;

  const passwordInputError = !validPassword && !!newPassword;
  const verifyPasswordInputError = !validVerifyPassword && !!verifyPassword;

  const disabled = !validPassword || !validVerifyPassword;

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowPasswordVerify = () =>
    setShowPasswordVerify(!showPasswordVerify);

  return (
    <GradientBackgroundContainer>
      <DialogBox>
        <DialogCustom isChangePasswordContainer>
          <Title>Space Plan Wizard</Title>
          <FormColumns>
            <TextField
              fullWidth
              placeholder="New Password"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={passwordInputError}
              helperText={
                (passwordInputError &&
                  'Password does not meet the requirements.') ||
                ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              placeholder="Verify New Password"
              variant="outlined"
              type={showPasswordVerify ? 'text' : 'password'}
              value={verifyPassword}
              onChange={(e) => setVerifyPassword(e.target.value)}
              error={verifyPasswordInputError}
              helperText={
                (verifyPasswordInputError && 'Passwords do not match') || ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPasswordVerify}
                      size="large"
                    >
                      {showPasswordVerify ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordRequirementsCard>
              <div className="password-requirements-title">
                PASSWORD REQUIREMENTS
              </div>
              <div className="content">
                <ul>
                  <li>
                    Contains at least eight (8) characters, one of which must be
                    a number
                  </li>
                  <li>Contains upper and lowercase letters</li>
                  <li>
                    Contains at least one special character: $%^&amp;*()_+|~-=\`
                    {'{}'}[]:&quot;;&apos;&lt;&gt;?,/.!@#
                  </li>
                </ul>
                <Img src={Security} alt="security-icon" />
              </div>
            </PasswordRequirementsCard>

            {updatePasswordSuccess === false && (
              <ErrorMessage bottom="62px">
                The password did not reset. Please try again.
              </ErrorMessage>
            )}

            <ButtonCustom
              variant="contained"
              disabled={disabled}
              onClick={() =>
                sendVerifyPassword(token, newPassword, verifyPassword)
              }
            >
              Reset Password
            </ButtonCustom>
          </FormColumns>
        </DialogCustom>
      </DialogBox>
    </GradientBackgroundContainer>
  );
};

const mapStateToProps = (state: IAppState): IResetPassword => ({
  newPassword: state.authReducer.newPassword,
  verifyPassword: state.authReducer.verifyPassword,
  updatePasswordSuccess: state.authReducer.updatePasswordSuccess,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  const { setNewPassword, setVerifyPassword, sendVerifyPassword } = actions;

  return bindActionCreators(
    { setNewPassword, setVerifyPassword, sendVerifyPassword },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
